/**
 * Letter format controls — header, footer, and (optionally) body typography.
 * Used for company-wide one-time letter format settings.
 */
import LetterRichEditor from "@/components/LetterRichEditor";

const LETTERHEAD_FONTS = [
  { value: "default", label: "Default (Unicode)" },
  { value: "arial", label: "Arial" },
  { value: "helvetica", label: "Helvetica" },
  { value: "calibri", label: "Calibri" },
  { value: "segoeui", label: "Segoe UI" },
  { value: "tahoma", label: "Tahoma" },
  { value: "verdana", label: "Verdana" },
  { value: "trebuchet", label: "Trebuchet MS" },
  { value: "centurygothic", label: "Century Gothic" },
  { value: "lucida", label: "Lucida Sans" },
  { value: "franklin", label: "Franklin Gothic" },
  { value: "corbel", label: "Corbel" },
  { value: "candara", label: "Candara" },
  { value: "times", label: "Times New Roman" },
  { value: "georgia", label: "Georgia" },
  { value: "garamond", label: "Garamond" },
  { value: "palatino", label: "Palatino Linotype" },
  { value: "bookantiqua", label: "Book Antiqua" },
  { value: "cambria", label: "Cambria" },
  { value: "constantia", label: "Constantia" },
  { value: "courier", label: "Courier New" },
  { value: "consolas", label: "Consolas" },
  { value: "lucidaconsole", label: "Lucida Console" },
];

export const FONT_CSS = {
  default: "Calibri, Candara, Segoe UI, sans-serif",
  arial: "Arial, Helvetica, sans-serif",
  helvetica: "Helvetica, Arial, sans-serif",
  calibri: "Calibri, Candara, Segoe UI, sans-serif",
  segoeui: '"Segoe UI", Tahoma, Geneva, sans-serif',
  tahoma: "Tahoma, Geneva, sans-serif",
  verdana: "Verdana, Geneva, sans-serif",
  trebuchet: '"Trebuchet MS", Helvetica, sans-serif',
  centurygothic: '"Century Gothic", CenturyGothic, AppleGothic, sans-serif',
  lucida: '"Lucida Sans Unicode", "Lucida Grande", sans-serif',
  franklin: '"Franklin Gothic Medium", "Arial Narrow", Arial, sans-serif',
  corbel: "Corbel, Calibri, sans-serif",
  candara: "Candara, Calibri, Segoe, sans-serif",
  times: '"Times New Roman", Times, serif',
  georgia: "Georgia, serif",
  garamond: "Garamond, Baskerville, 'Times New Roman', serif",
  palatino: '"Palatino Linotype", Palatino, "Book Antiqua", serif',
  bookantiqua: '"Book Antiqua", Palatino, serif',
  cambria: "Cambria, Georgia, serif",
  constantia: "Constantia, Georgia, serif",
  courier: '"Courier New", Courier, monospace',
  consolas: "Consolas, 'Courier New', monospace",
  lucidaconsole: '"Lucida Console", Monaco, monospace',
};

/** Shared show/hide fields — same options for header and footer. */
export const LETTERHEAD_SHOW_FIELDS = [
  ["logo", "Show logo"],
  ["company_name", "Show company name"],
  ["address", "Show company address"],
  ["cin", "Show CIN"],
  ["gstin", "Show GSTIN"],
  ["pan", "Show PAN"],
  ["phone", "Show phone"],
  ["email", "Show email"],
  ["website", "Show website"],
  ["confidential", "Show confidentiality line"],
];

export const DEFAULT_LETTERHEAD = {
  layout: "logo_left",
  // Header toggles (legacy aliases: show_logo, show_legal_name, show_address, show_contact)
  show_logo: true,
  show_legal_name: true,
  show_address: true,
  show_contact: true, // legacy = phone + email
  show_header_phone: true,
  show_header_email: true,
  show_header_cin: false,
  show_header_gstin: false,
  show_header_pan: false,
  show_header_website: false,
  show_header_confidential: false,
  show_letter_title: false,
  accent_color: "#226DB4",
  name_font: "default",
  name_font_size: 14,
  name_bold: true,
  detail_font: "default",
  detail_font_size: 9,
  detail_color: "#334155",
  custom_header_html: "",
  footer_layout: "center",
  show_footer_logo: false,
  show_footer_company_name: false,
  show_footer_address: false,
  show_footer_cin: true,
  show_footer_gstin: true,
  show_footer_pan: true,
  show_footer_phone: false,
  show_footer_email: false,
  show_footer_website: false,
  show_footer_confidential: true,
  custom_footer_html: "",
  footer_font: "default",
  footer_font_size: 7,
  footer_color: "#64748b",
  footer_bold: false,
  // Body text defaults (Calibri + comfortable spacing in PDF/preview)
  body_font: "calibri",
  body_font_size: 11,
  body_color: "#0f172a",
};

function headerShowKey(field) {
  if (field === "logo") return "show_logo";
  if (field === "company_name") return "show_legal_name";
  if (field === "address") return "show_address";
  return `show_header_${field}`;
}

function footerShowKey(field) {
  if (field === "company_name") return "show_footer_company_name";
  return `show_footer_${field}`;
}

function isShowChecked(lh, zone, field) {
  if (zone === "header") {
    const key = headerShowKey(field);
    if (field === "phone" || field === "email") {
      if (lh[key] != null) return !!lh[key];
      return !!lh.show_contact; // legacy fallback
    }
    return !!lh[key];
  }
  return !!lh[footerShowKey(field)];
}

function setShowChecked(lh, zone, field, checked) {
  if (zone === "header") {
    const key = headerShowKey(field);
    const patch = { [key]: checked };
    if (field === "phone" || field === "email") {
      const phone = field === "phone" ? checked : isShowChecked(lh, "header", "phone");
      const email = field === "email" ? checked : isShowChecked(lh, "header", "email");
      patch.show_contact = !!(phone || email);
    }
    return patch;
  }
  return { [footerShowKey(field)]: checked };
}

/** Prefer Calibri stack when letter format uses default/helvetica/arial aliases. */
export function resolveLetterBodyFontCss(fontKey) {
  const key = String(fontKey || "calibri").toLowerCase();
  if (key === "default" || key === "helvetica" || key === "arial" || key === "calibri") {
    return FONT_CSS.calibri || FONT_CSS.default;
  }
  return FONT_CSS[key] || FONT_CSS.calibri || FONT_CSS.default;
}

export const DEFAULT_LETTER_FORMAT = {
  letterhead: { ...DEFAULT_LETTERHEAD },
  body_font: "calibri",
  body_font_size: 11,
  body_color: "#0f172a",
};

export function buildWorkingFormat(tplFormat, companyLetterhead) {
  const company = { ...DEFAULT_LETTERHEAD, ...(companyLetterhead || {}) };
  const base = tplFormat && typeof tplFormat === "object" ? tplFormat : {};
  return {
    body_font: base.body_font || company.body_font || DEFAULT_LETTER_FORMAT.body_font,
    body_font_size: base.body_font_size || company.body_font_size || DEFAULT_LETTER_FORMAT.body_font_size,
    body_color: base.body_color || company.body_color || DEFAULT_LETTER_FORMAT.body_color,
    letterhead: {
      ...DEFAULT_LETTERHEAD,
      ...company,
      ...(base.letterhead || {}),
    },
  };
}

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

function FontSelect({ value, onChange, testid }) {
  return (
    <select className={inputCls} value={value || "default"} onChange={onChange} data-testid={testid}>
      {LETTERHEAD_FONTS.map((f) => (
        <option key={f.value} value={f.value}>{f.label}</option>
      ))}
    </select>
  );
}

/**
 * @param {{ letterhead: object }} value — letterhead slice only when showBody=false
 * @param {(lh: object) => void} onLetterheadChange
 * @param {{ body_font, body_font_size, body_color }} [bodyFormat]
 * @param {(patch: object) => void} [onBodyChange]
 * @param {'header'|'footer'|'body'} tab
 * @param {(t: string) => void} onTabChange
 * @param {boolean} [showBody]
 */
export default function LetterFormatPanel({
  letterhead,
  onLetterheadChange,
  bodyFormat,
  onBodyChange,
  tab = "header",
  onTabChange,
  showBody = true,
}) {
  const lh = letterhead || DEFAULT_LETTERHEAD;
  const setLh = (patch) => onLetterheadChange({ ...lh, ...patch });
  const body = bodyFormat || DEFAULT_LETTER_FORMAT;

  const tabs = showBody
    ? [
        ["header", "Header"],
        ["footer", "Footer"],
        ["body", "Letter text"],
      ]
    : [
        ["header", "Header"],
        ["footer", "Footer"],
      ];

  return (
    <div className="space-y-4 text-sm" data-testid="letter-format-panel">
      <div className="flex gap-1 border-b border-slate-200">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => onTabChange?.(key)}
            className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition ${
              tab === key
                ? "border-[#226DB4] text-[#226DB4]"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`format-tab-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "header" && (
        <section className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Layout</label>
            <select
              className={inputCls}
              value={lh.layout}
              onChange={(e) => setLh({ layout: e.target.value })}
              data-testid="letterhead-layout"
            >
              <option value="logo_left">Logo left + company details</option>
              <option value="logo_center">Logo centered above details</option>
              <option value="text_only">Text only (no logo)</option>
              <option value="none">No letterhead</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {LETTERHEAD_SHOW_FIELDS.map(([field, label]) => (
              <label key={field} className="flex items-center gap-2 text-slate-700">
                <input
                  type="checkbox"
                  checked={isShowChecked(lh, "header", field)}
                  disabled={lh.layout === "none"}
                  onChange={(e) => setLh(setShowChecked(lh, "header", field, e.target.checked))}
                  data-testid={`letterhead-header-${field}`}
                />
                {label}
              </label>
            ))}
          </div>

          <div className="rounded border border-slate-200 bg-slate-50 p-3 space-y-3">
            <div className="text-xs font-semibold text-slate-700">Company name</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Font</label>
                <FontSelect
                  value={lh.name_font}
                  onChange={(e) => setLh({ name_font: e.target.value })}
                  testid="letterhead-name-font"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Size</label>
                <select
                  className={inputCls}
                  value={lh.name_font_size || 14}
                  onChange={(e) => setLh({ name_font_size: Number(e.target.value) })}
                >
                  {[10, 12, 14, 16, 18, 20].map((n) => (
                    <option key={n} value={n}>{n} pt</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Colour</label>
                <input
                  type="color"
                  className="h-10 w-full border border-slate-200 rounded cursor-pointer bg-white"
                  value={lh.accent_color || "#226DB4"}
                  onChange={(e) => setLh({ accent_color: e.target.value })}
                />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-slate-700">
                  <input
                    type="checkbox"
                    checked={lh.name_bold !== false}
                    onChange={(e) => setLh({ name_bold: e.target.checked })}
                  />
                  Bold
                </label>
              </div>
            </div>
          </div>

          <div className="rounded border border-slate-200 bg-slate-50 p-3 space-y-3">
            <div className="text-xs font-semibold text-slate-700">Address / contact</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Font</label>
                <FontSelect
                  value={lh.detail_font}
                  onChange={(e) => setLh({ detail_font: e.target.value })}
                  testid="letterhead-detail-font"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Size</label>
                <select
                  className={inputCls}
                  value={lh.detail_font_size || 9}
                  onChange={(e) => setLh({ detail_font_size: Number(e.target.value) })}
                >
                  {[7, 8, 9, 10, 11, 12, 13, 14].map((n) => (
                    <option key={n} value={n}>{n} pt</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Colour</label>
                <input
                  type="color"
                  className="h-10 w-full border border-slate-200 rounded cursor-pointer bg-white"
                  value={lh.detail_color || "#334155"}
                  onChange={(e) => setLh({ detail_color: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Extra header content (optional)</label>
            <LetterRichEditor
              value={lh.custom_header_html || ""}
              onChange={(html) => setLh({ custom_header_html: html })}
              minHeight={120}
              compact
              testid="letterhead-header-rich"
              placeholder="Optional rich header lines… e.g. Registered Office: {{company_address}}"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Letter title is not part of the header — insert {"{{letter_title}}"} in the template body instead.
              Supports bullets, tables, fonts — same tools as the letter body.
            </p>
          </div>
        </section>
      )}

      {tab === "footer" && (
        <section className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Layout</label>
            <select
              className={inputCls}
              value={lh.footer_layout || "center"}
              onChange={(e) => setLh({ footer_layout: e.target.value })}
              data-testid="letterhead-footer-layout"
            >
              <option value="center">Centered</option>
              <option value="left">Left aligned</option>
              <option value="none">No footer</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {LETTERHEAD_SHOW_FIELDS.map(([field, label]) => (
              <label key={field} className="flex items-center gap-2 text-slate-700">
                <input
                  type="checkbox"
                  checked={isShowChecked(lh, "footer", field)}
                  disabled={lh.footer_layout === "none"}
                  onChange={(e) => setLh(setShowChecked(lh, "footer", field, e.target.checked))}
                  data-testid={`letterhead-footer-${field}`}
                />
                {label}
              </label>
            ))}
          </div>

          {lh.footer_layout !== "none" && (
            <div className="rounded border border-slate-200 bg-slate-50 p-3 space-y-3">
              <div className="text-xs font-semibold text-slate-700">Footer text</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Font</label>
                  <FontSelect
                    value={lh.footer_font}
                    onChange={(e) => setLh({ footer_font: e.target.value })}
                    testid="letterhead-footer-font"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Size</label>
                  <select
                    className={inputCls}
                    value={lh.footer_font_size || 7}
                    onChange={(e) => setLh({ footer_font_size: Number(e.target.value) })}
                    data-testid="letterhead-footer-size"
                  >
                    {[6, 7, 8, 9, 10, 11, 12].map((n) => (
                      <option key={n} value={n}>{n} pt</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Colour</label>
                  <input
                    type="color"
                    className="h-10 w-full border border-slate-200 rounded cursor-pointer bg-white"
                    value={lh.footer_color || "#64748b"}
                    onChange={(e) => setLh({ footer_color: e.target.value })}
                    data-testid="letterhead-footer-color"
                  />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 text-slate-700">
                    <input
                      type="checkbox"
                      checked={!!lh.footer_bold}
                      onChange={(e) => setLh({ footer_bold: e.target.checked })}
                    />
                    Bold
                  </label>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Extra footer content (optional)</label>
            <LetterRichEditor
              value={lh.custom_footer_html || ""}
              onChange={(html) => setLh({ custom_footer_html: html })}
              minHeight={100}
              compact
              testid="letterhead-footer-rich"
              placeholder="Optional footer lines… e.g. www.company.com | hr@company.com"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Page footer on PDF shows text lines (formatting simplified). Prefer layout toggles above for CIN/GSTIN.
            </p>
          </div>
        </section>
      )}

      {tab === "body" && showBody && (
        <section className="space-y-3">
          <p className="text-[11px] text-slate-500">
            Default look for letter body text on PDF. Inline toolbar styles in the editor still override selected text.
          </p>
          <div className="rounded border border-slate-200 bg-slate-50 p-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Font</label>
                <FontSelect
                  value={body.body_font}
                  onChange={(e) => onBodyChange?.({ body_font: e.target.value })}
                  testid="format-body-font"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Size</label>
                <select
                  className={inputCls}
                  value={body.body_font_size || 11}
                  onChange={(e) => onBodyChange?.({ body_font_size: Number(e.target.value) })}
                  data-testid="format-body-size"
                >
                  {[8, 9, 10, 11, 12, 13, 14, 16, 18].map((n) => (
                    <option key={n} value={n}>{n} pt</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Colour</label>
                <input
                  type="color"
                  className="h-10 w-full border border-slate-200 rounded cursor-pointer bg-white"
                  value={body.body_color || "#0f172a"}
                  onChange={(e) => onBodyChange?.({ body_color: e.target.value })}
                  data-testid="format-body-color"
                />
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
